import { ReactNode } from 'react'

import { AstraChipProps, AstraHeading, AstraHoverCard, AstraParagraph, astraTwMerge } from '~/astra'
import { useSurgeryTypesByName } from '~/storm'
import { ResolvedConstraintObject } from '~/utils/blockEvaluation'

import { RuleValuesChip } from './RuleValuesChip'

const HoverContent = (constraint: Pick<ResolvedConstraintObject, 'surgeryTypeGroupLabel'>) => {
    const surgeryTypes = useSurgeryTypesByName(constraint.surgeryTypeGroupLabel)

    if (!surgeryTypes?.length) {
        return null
    }

    /**
     * There could be cases where we are provided with multiple surgery types with identical names from DIPS.
     * This ensures that we only display distinct names in the hover card content list.
     */
    const sortedDistinctSurgeryTypeNames = [...new Set(surgeryTypes.map(surgeryType => surgeryType.name))].sort()

    return (
        <div className="flex w-fit flex-col gap-3 py-4">
            <AstraHeading as="h3" weight="medium" className="px-4">
                {constraint.surgeryTypeGroupLabel}
            </AstraHeading>
            <hr className="h-px bg-neutral-rest" />
            <ul className="flex max-h-[50vh] list-disc flex-col gap-2 overflow-y-auto px-4">
                {sortedDistinctSurgeryTypeNames.map(surgeryType => (
                    <li key={`surgery-type-${surgeryType}`} className="ml-4">
                        <AstraParagraph>{surgeryType}</AstraParagraph>
                    </li>
                ))}
            </ul>
        </div>
    )
}

const wrapperStyles = 'rounded-md border border-transparent  text-left transition-all hover:border-strong-on-bg-fill-brand-rest max-w-full'

export const RuleValueChipWrapper = ({
    resolved,
    children,
    fullWidth = true,
    variant = 'filled',
}: {
    resolved: Pick<ResolvedConstraintObject, 'groupLevel' | 'surgeryTypeGroupLabel'>
    children: ReactNode
    fullWidth?: boolean
    variant?: AstraChipProps['variant']
}) => {
    // Hover card not displayed if there are no surgery types
    if (resolved.groupLevel === 'none') {
        return children
    }

    return (
        <AstraHoverCard className="min-w-max" hoverContent={<HoverContent {...resolved} />} placement="right" noPadding showArrow>
            <button className={astraTwMerge(wrapperStyles, !fullWidth ? 'w-fit' : '', variant === 'filled' ? 'bg-neutral-rest' : '')}>{children}</button>
        </AstraHoverCard>
    )
}
type Props = {
    resolvedConstraints: ResolvedConstraintObject[]
    formatter?: (con: ResolvedConstraintObject) => string
    fullWidth?: boolean
    variant?: AstraChipProps['variant']
    omitIcon?: boolean
}

export const RuleValuesChips = ({ resolvedConstraints, formatter, fullWidth = true, variant = 'filled', omitIcon }: Props) =>
    resolvedConstraints.map((resolved, index) => (
        <RuleValueChipWrapper resolved={resolved} key={index} fullWidth={fullWidth} variant={variant}>
            <RuleValuesChip
                variant={variant}
                fullWidth={fullWidth}
                text={formatter?.(resolved) ?? `${resolved.value} ${resolved.surgeryTypeGroupLabel}`}
                groupLevel={omitIcon ? 'none' : resolved.groupLevel}
                ageCategory={resolved.ageCategory}
            />
        </RuleValueChipWrapper>
    ))
